import React, { useEffect } from 'react';
import { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import dayjs from 'dayjs';

import { dateCellRenderer } from '@/pages/ContractDetails/ContractLines/grid/helper';
import { DEFAULT_DATE_PATTERN } from '@/config/common.config';
import { validator } from './validation/validator';
import { VARIABLE_STATES_COLORS } from '@/config/statuses.config';
import { useGetReasonCodes } from '@/pages/SystemAdmin/ReasonCodes/hooks/useGetReasonCodes';
import { DNR_TYPE } from '@/config/reasonTypes.config';
import NoFurtherBillingToggle from './NoFurtherBillingToggle/NoFurtherBillingToggle';
import { FormattingUtils } from '@/utils/Formatting.utils';
import { Pill } from '@/components/common/Pill/Pill';
import { ContractStatusFormatted } from '@/pages/ContractDetails/ContractLines/grid/variableContractLinescolumns';
import { VariableContractLineOutput } from '@/graphql/contracts/_generated_/contracts';

type Reason = {
  code: string;
  name: string;
};

export function useVariableColumns(
  dnrContractLines: VariableContractLineOutput[],
  getValidationErrors?: (colDef: any, data: any, context: any) => string[]
): ColDef<VariableContractLineOutput>[] {
  const { getReasonCodes, isLoading, reasonCodes } = useGetReasonCodes();

  useEffect(() => {
    getReasonCodes(DNR_TYPE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCellRendererParams = ({ colDef, data, context }: ICellRendererParams) => ({
    errors: getValidationErrors 
      ? getValidationErrors(colDef, data, { contract: context?.contract })
      : validator.getErrorsAt(colDef?.field!, data, { contract: context?.contract }),
    errorTitle: colDef!.headerName
  });

  return [
    {
      colId: 'status',
      headerName: 'Status',
      field: 'status',
      autoHeight: true,
      maxWidth: 140,
      suppressSizeToFit: true,
      cellStyle: {
        display: 'flex',
        justifyContent: 'center',
      },
      cellRenderer: ({ value }:
      ICellRendererParams<VariableContractLineOutput, VariableContractLineOutput['status']>) => {
        const color = value && VARIABLE_STATES_COLORS[value];

        return (
          <Pill background={color ?? undefined} size="large">
            {value && ContractStatusFormatted[value]}
          </Pill>
        );
      },
    },
    {
      colId: 'stockCode',
      headerName: 'Stock Code',
      field: 'stockCode',
      initialWidth: 295,
      minWidth: 280,
      cellRenderer: ({ data }: ICellRendererParams) => {
        return `${data?.stockCode} - ${data?.stockName}`;
      }
    },
    {
      colId: 'reasonCode',
      headerName: 'Reason Name',
      field: 'statusChangeReason',
      initialWidth: 220,
      minWidth: 180,
      editable: true,
      cellRenderer: ({ data }: ICellRendererParams) => {
        return data?.statusChangeReason?.name ?? data?.statusChangeReasonName;
      },
      cellEditor: 'DropdownEditor',
      cellEditorParams: ({ data }: ICellRendererParams<VariableContractLineOutput>) => {
        // To show old reason inside dropdown even if it was already disabled
        // (user can accidentally change reason and want to return back old one)
        const contractLine = dnrContractLines.find((cl) => cl.iD === data?.iD);
        const reasons = [...reasonCodes.map(reason => ({ code: reason.code, name: reason.name }))];
        const isOldReasonAlreadyInDropdown =
          Boolean(reasons.find((el) => el.code === contractLine?.statusChangeReason));

        if (!isOldReasonAlreadyInDropdown) {
          if (contractLine?.statusChangeReason && contractLine?.statusChangeReasonName)
            reasons.push({
              code: contractLine.statusChangeReason,
              name: contractLine.statusChangeReasonName,
            });
        }

        return {
          options: reasons,
          getOptionValue: (option: Reason) => option.code,
          getOptionLabel: (option: Reason) => option.name,
          saveOnChange: true,
          isLoading: isLoading
        };
      },
      valueFormatter: ({ value }: ValueFormatterParams<VariableContractLineOutput, ReasonCode>) => {
        return value?.name as string;
      },
    },
    {
      colId: 'dnrDate',
      headerName: 'DNR Date',
      field: 'statusChangeDate',
      initialWidth: 180,
      minWidth: 180,
      editable: true,
      cellEditor: 'DatepickerEditor',
      cellRenderer: 'CellRenderer',
      cellRendererParams: getCellRendererParams,
      valueFormatter: ({ value }: ValueFormatterParams<VariableContractLineOutput>) => {
        if (value && !Number.isNaN(Date.parse(value))) {
          return `${dayjs(value).format(DEFAULT_DATE_PATTERN)}`;
        }
        return '';
      },
      cellEditorParams: () => ({
        saveOnChange: true,
      }),
    },
    {
      colId: 'comments',
      headerName: 'Comments',
      field: 'statusChangeComment',
      initialWidth: 140,
      minWidth: 140,
      editable: true,
      cellRenderer: 'CellRenderer',
      cellRendererParams: getCellRendererParams,
      valueSetter: (params: any) => {
        params.data.statusChangeComment = params.newValue.trim();
        return true;
      },
    },
    {
      colId: 'contractEndDate',
      headerName: 'Contract End Date',
      initialWidth: 150,
      minWidth: 150,
      cellRenderer: ({ context }: ICellRendererParams) => {
        const contractEndDate = (context.contract as Contract).contractEndDate;
        return contractEndDate ? dateCellRenderer({ value: contractEndDate }) : '';
      }
    },
    {
      colId: 'term',
      headerName: 'Contract Term',
      initialWidth: 125,
      minWidth: 125,
      cellRenderer: ({ context }: ICellRendererParams) => {
        const term = (context.contract as Contract).term;
        return term ? `${term} ${FormattingUtils.pluralize(term, 'month')}` : '';
      }
    },
    {
      colId: 'noFurtherBilling',
      field: 'noFurtherBilling',
      headerName: 'No Further Billing',
      initialWidth: 150,
      minWidth: 150,
      cellRenderer: NoFurtherBillingToggle,
      onCellValueChanged: ({ node }) => {
        node?.setDataValue('noFurtherBillingDate', null);
      }
    },
    {
      colId: 'noFurtherBillingDate',
      field: 'noFurtherBillingDate',
      headerName: 'Date (No Further Billing)',
      editable: ({ data }) => Boolean(data?.noFurtherBilling),
      cellRenderer: 'CellRenderer',
      cellEditor: 'DatepickerEditor',
      cellEditorParams: () => ({
        saveOnChange: true,
      }),
      cellRendererParams: getCellRendererParams,
      valueFormatter: ({ value }: ValueFormatterParams<VariableContractLineOutput>) => {
        if (value && !Number.isNaN(Date.parse(value))) {
          return `${dayjs(value).format(DEFAULT_DATE_PATTERN)}`;
        }
        return '';
      },
    }
  ];
}
